/***
 *
 *   AUTHENTICATION
 *   Auth provider to manage auth functions throughout
 *   the application. <PrivateRoute> component to
 *   protect internal application routes from unauthenticated
 *   access.
 *
 **********/

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { AuthContext, type AuthUser, type PermissionFlags } from './authContext';
import * as Sentry from '@sentry/react';
import { UMLoader } from '@urbanmetrix/um-kit';
import { AuthBootstrapError, bootstrap, createClient, unwrapAuth, type AuthBootstrapResult } from '@urbanmetrix/sdk';
import { clearPendingInvitationId, getPendingInvitationId } from '../lib/pending-invitation';
import { isMaintenanceResponse, MAINTENANCE_PATH, redirectToMaintenance } from 'api/maintenanceApi';

export const SIGN_UP_EMAIL_KEY = 'signUpEmail';

interface BillingState {
  plan?: boolean;
  subscription?: string | null;
}

interface MaybeApiError {
  code?: string;
  status?: number;
  error?: { code?: string };
  response?: { status?: number; data?: { code?: string } };
}

function buildPermissionFlags(appRole: string, organizationRole: string | null): PermissionFlags {
  const isAdminAppRole = appRole === 'admin';
  const isDevRole = appRole === 'dev';
  const isSupportAppRole = appRole === 'support';

  const isOwner = isAdminAppRole || organizationRole === 'owner';
  const isAdmin = isOwner || organizationRole === 'admin';
  const isEditor = isAdmin || organizationRole === 'editor';
  const canViewProjects = isEditor || organizationRole === 'viewer' || isDevRole || isSupportAppRole;

  return {
    master: isAdminAppRole,
    owner: isOwner,
    admin: isAdmin,
    developer: isAdminAppRole || isDevRole,
    editor: isEditor,
    viewer: canViewProjects,
    support: isSupportAppRole,
    dev: isDevRole,
    user: appRole === 'user',
  };
}

function buildGlobalPermissions() {
  return {
    master: buildPermissionFlags('admin', 'owner'),
    owner: buildPermissionFlags('user', 'owner'),
    admin: buildPermissionFlags('user', 'admin'),
    editor: buildPermissionFlags('user', 'editor'),
    viewer: buildPermissionFlags('user', 'viewer'),
    developer: buildPermissionFlags('dev', null),
    admin_app: buildPermissionFlags('admin', null),
    support: buildPermissionFlags('support', null),
    dev: buildPermissionFlags('dev', null),
    user: buildPermissionFlags('user', null),
  };
}

function asAuthError(error: unknown): MaybeApiError {
  return (error ?? {}) as MaybeApiError;
}

function getErrorStatus(error: unknown): number | undefined {
  const apiError = asAuthError(error);
  return apiError.status || apiError.response?.status;
}

function getErrorCode(error: unknown): string | undefined {
  const apiError = asAuthError(error);
  return apiError.code || apiError.error?.code || apiError.response?.data?.code;
}

function getApiBaseUrl() {
  return import.meta.env.VITE_SERVER_URL || (typeof window !== 'undefined' ? window.location.origin : '');
}

function normalizeSessionUser(
  result: AuthBootstrapResult,
  needsOrganizationSetup = false,
  billingState: BillingState = {}
): AuthUser {
  const appRole = result.user.role || 'user';

  return {
    ...result.user,
    token: result.session.session?.id || true,
    verified: result.user.emailVerified,
    appRole,
    organizationRole: result.organizationRole,
    permission: result.organizationRole || appRole,
    permissions: buildPermissionFlags(appRole, result.organizationRole),
    session: result.session.session ?? undefined,
    requiresOrganizationSetup: needsOrganizationSetup,
    organizations: result.organizations,
    accounts: [],
    account_id: null,
    subscription: billingState.subscription ?? null,
    plan: billingState.plan ?? true,
  };
}

function setSentryInfo(user: AuthUser | null) {
  const user_id = user?.id;
  const username = user?.name;
  const account_id = user?.account_id;
  const account_name = user?.accounts?.find((a) => a.id === account_id)?.name;
  const user_email = user?.email;
  const sentryId = account_id ? `${user_id}-${account_id}` : user_id;
  const sentryUsername = account_name ? `${username} - ${account_name}` : username;

  if (!sentryId) {
    Sentry.setUser(null);
    return;
  }

  Sentry.setUser({ id: sentryId, username: sentryUsername, email: user_email });
  Sentry.setTag('user.id', user_id);
  Sentry.setTag('user.name', username);
  if (account_id) Sentry.setTag('account.id', account_id);
  if (account_name) Sentry.setTag('account.name', account_name);
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isHydratingUser, setIsHydratingUser] = useState(true);
  const [hasBootstrappedSession, setHasBootstrappedSession] = useState(false);
  const [pendingInvitationError, setPendingInvitationError] = useState<string | null>(null);
  const [bootstrapKey, setBootstrapKey] = useState(0);
  const globalPermissions = useMemo(() => buildGlobalPermissions(), []);
  const client = useMemo(() => createClient(getApiBaseUrl()), []);
  const lastResultRef = useRef<AuthBootstrapResult | null>(null);

  const applyAuthenticatedResult = useCallback((result: AuthBootstrapResult) => {
    lastResultRef.current = result;
    const needsOrganizationSetup = !result.organizationId;
    const nextUser = normalizeSessionUser(result, needsOrganizationSetup, {
      plan: needsOrganizationSetup ? true : result.hasActiveSubscription,
      subscription: result.subscription?.status || null,
    });

    setUser(nextUser);
    setSentryInfo(nextUser);
    setHasBootstrappedSession(true);
    setIsHydratingUser(false);
  }, []);

  const handleUnauthenticated = useCallback((error: AuthBootstrapError) => {
    setUser(null);
    setSentryInfo(null);
    setHasBootstrappedSession(true);
    setIsHydratingUser(false);

    if (isMaintenanceResponse(error.cause as { status?: number; data?: unknown } | null | undefined)) {
      redirectToMaintenance();
    }
  }, []);

  const handleBootstrapError = useCallback((error: AuthBootstrapError) => {
    if (isMaintenanceResponse(error.cause as { status?: number; data?: unknown } | null | undefined)) {
      redirectToMaintenance();
      return;
    }

    if (error.status === 'role-error') {
      const status = getErrorStatus(error.cause);
      const errorCode = getErrorCode(error.cause);
      if (status === 403 || errorCode === 'FORBIDDEN') {
        client.auth.signOut().finally(() => {
          setUser(null);
          setSentryInfo(null);
          setHasBootstrappedSession(true);
          setIsHydratingUser(false);
          window.location.href = '/login';
        });
        return;
      }
    }

    console.error('Error hydrating user:', error);
    setHasBootstrappedSession(true);
    setIsHydratingUser(false);
  }, []);

  const refreshSession = useCallback(async () => {
    setIsHydratingUser(true);
    setBootstrapKey((currentKey) => currentKey + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function hydrateSession() {
      try {
        const pendingInvitationId = getPendingInvitationId();
        if (pendingInvitationId) {
          try {
            unwrapAuth(
              await client.auth.organization.acceptInvitation({
                invitationId: pendingInvitationId,
              })
            );
            clearPendingInvitationId();
          } catch (error) {
            if (getErrorCode(error) === 'INVITATION_NOT_FOUND') {
              setPendingInvitationError(
                'The invitation has expired or is not valid. Please ask the admin to invite you again.'
              );
              clearPendingInvitationId();
            }
          }
        }

        const result = await bootstrap({ client });
        if (!cancelled) applyAuthenticatedResult(result);
      } catch (error) {
        if (cancelled) return;

        const authError =
          error instanceof AuthBootstrapError
            ? error
            : new AuthBootstrapError(
                getErrorStatus(error) === 401 ? 'unauthenticated' : 'error',
                error instanceof Error ? error.message : 'AUTH_BOOTSTRAP_FAILED',
                error
              );

        if (authError.status === 'unauthenticated') {
          handleUnauthenticated(authError);
        } else {
          handleBootstrapError(authError);
        }
      }
    }

    hydrateSession();
    return () => {
      cancelled = true;
    };
  }, [bootstrapKey, client, applyAuthenticatedResult, handleBootstrapError, handleUnauthenticated]);

  async function signin(res?: { data?: AuthUser } | unknown) {
    const resWithData = res as { data?: AuthUser } | undefined;
    try {
      const session = await client.auth.getSession();
      if (session?.data?.user && lastResultRef.current) {
        const activeOrganizationId =
          (session.data.session as { activeOrganizationId?: string | null } | null | undefined)?.activeOrganizationId ||
          null;
        const nextUser = normalizeSessionUser(
          {
            ...lastResultRef.current,
            session: session.data,
            user: session.data.user,
            organizationId: activeOrganizationId,
          },
          !activeOrganizationId,
          {
            plan: activeOrganizationId ? lastResultRef.current.hasActiveSubscription : true,
            subscription: lastResultRef.current.subscription?.status || null,
          }
        );

        setUser(nextUser);
        window.location.href = '/dashboard';
        return;
      }

      if (resWithData?.data) {
        const fallbackUser: AuthUser = {
          ...resWithData.data,
          appRole: resWithData.data.role || 'user',
          organizationRole: resWithData.data.organizationRole || null,
          permission: resWithData.data.organizationRole || resWithData.data.role || 'user',
          permissions: buildPermissionFlags(resWithData.data.role || 'user', resWithData.data.organizationRole || null),
          plan: resWithData.data.plan ?? true,
          subscription: resWithData.data.subscription ?? null,
          accounts: resWithData.data.accounts || [],
          account_id: resWithData.data.account_id || null,
        };
        setUser(fallbackUser);
        window.location.href = '/dashboard';
        return;
      }
    } catch (error) {
      console.error('Error during sign-in:', error);
      if (isMaintenanceResponse(error as { status?: number; data?: unknown } | null | undefined)) {
        redirectToMaintenance();
        return;
      }
      alert('An error occurred during login. Please try again.');
    }
  }

  async function signout() {
    try {
      await client.auth.signOut();
      setUser(null);
      window.location.href = '/login';
    } catch (error) {
      console.error('Error during signout:', error);
      alert('Signout failed. Please try again.');
    }
  }

  function update(data: Record<string, unknown>) {
    setUser((currentUser) => updateUserState(currentUser, data));
  }

  function updateUserState(currentUser: AuthUser | null, data: Record<string, unknown>): AuthUser | null {
    if (!currentUser) return currentUser;

    const nextUser: AuthUser = { ...currentUser };

    for (let key in data) {
      if (Array.isArray(data[key])) {
        nextUser[key] = data[key];
      } else if (data[key] && typeof data[key] === 'object') {
        nextUser[key] = {
          ...(currentUser[key] || {}),
          ...data[key],
        };
      } else {
        nextUser[key] = data[key];
      }
    }

    return nextUser;
  }

  const contextValue = {
    user: user,
    isAuthenticated: !!user,
    isLoading: isHydratingUser,
    refreshSession,
    signin: signin as (res?: unknown) => Promise<void>,
    signout: signout,
    update: update,
    globalPermissions,
    permission: user?.permissions || null,
    isHydratingUser,
    hasBootstrappedSession,
    pendingInvitationError,
    clearPendingInvitationError: () => setPendingInvitationError(null),
  };

  // The maintenance page must be available when the authentication API is
  // unavailable, so it cannot wait for session bootstrap to finish.
  if (typeof window !== 'undefined' && window.location.pathname === MAINTENANCE_PATH) {
    return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
  }

  if (!user && !hasBootstrappedSession) {
    return (
      <div className="auth-loader-screen">
        <UMLoader />
      </div>
    );
  }

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}
